import { useEffect, useState } from "react";
import { Cohort } from "../../model/cohort";
import { CalendarSessionOverview } from "../../model/calendar-session-overview";
import { getCalendarSessions } from "../../service/session-attendance-service";
import { useToast } from "../../../../context/toast/toast-provider";
import dayjs, { Dayjs } from "dayjs";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; // skip Sunday

export default function CohortCalendar({ cohort }: { cohort: Cohort }) {
    const [loading, setLoading] = useState(false);
    const [sessions, setSessions] = useState<CalendarSessionOverview[]>([]);
    const [weekStart, setWeekStart] = useState<Dayjs>(dayjs().startOf('week').add(1, 'day')); // Monday

    const toast = useToast();
    const hasCalendar = !!cohort.googleCalendarId;

    useEffect(() => {
        if (hasCalendar && cohort.id) {
            loadSessions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekStart, cohort.id, cohort.googleCalendarId]);

    const loadSessions = () => {
        if (!cohort.id) return;
        setLoading(true);
        const from = weekStart.format('YYYY-MM-DD');
        const to = weekStart.add(5, 'day').format('YYYY-MM-DD'); // Mon-Sat
        getCalendarSessions(cohort.id, from, to).then(result => {
            setSessions(result ?? []);
            setLoading(false);
        }).catch(() => {
            toast.addToast("Failed to load calendar sessions", "error");
            setLoading(false);
        });
    };

    const getSessionsForDay = (dayIndex: number): CalendarSessionOverview[] => {
        const dayDate = weekStart.add(dayIndex, 'day').format('YYYY-MM-DD');
        return sessions.filter(s => dayjs(s.day).format('YYYY-MM-DD') === dayDate);
    };

    if (!hasCalendar) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography>No Google Calendar configured for this cohort. Set the Google Calendar ID in the Edit tab.</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton onClick={() => setWeekStart(weekStart.subtract(7, 'day'))}>
                    <ChevronLeftIcon />
                </IconButton>
                <IconButton onClick={() => setWeekStart(dayjs().startOf('week').add(1, 'day'))}>
                    <TodayIcon />
                </IconButton>
                <IconButton onClick={() => setWeekStart(weekStart.add(7, 'day'))}>
                    <ChevronRightIcon />
                </IconButton>
                <Typography variant="h6" sx={{ ml: 1 }}>
                    {weekStart.format('MMM D')} - {weekStart.add(5, 'day').format('MMM D, YYYY')}
                </Typography>
                {loading && <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading...</Typography>}
            </Box>

            {/* Weekly Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5, minHeight: 400 }}>
                {/* Day headers */}
                {DAYS.map((day, i) => {
                    const date = weekStart.add(i, 'day');
                    const isToday = date.isSame(dayjs(), 'day');
                    return (
                        <Box key={day} sx={{
                            textAlign: 'center',
                            p: 1,
                            borderBottom: '2px solid',
                            borderColor: isToday ? 'primary.main' : 'divider',
                            bgcolor: isToday ? 'primary.50' : undefined
                        }}>
                            <Typography variant="caption" fontWeight="bold">{day}</Typography>
                            <Typography variant="body2" color={isToday ? 'primary.main' : 'text.secondary'}>
                                {date.format('D')}
                            </Typography>
                        </Box>
                    );
                })}

                {/* Day cells with events */}
                {DAYS.map((_, i) => {
                    const daySessions = getSessionsForDay(i);
                    return (
                        <Box key={`cell-${i}`} sx={{
                            minHeight: 120,
                            p: 0.5,
                            borderRight: i < 5 ? '1px solid' : undefined,
                            borderColor: 'divider',
                            overflow: 'auto'
                        }}>
                            {daySessions.map((session, idx) => (
                                <Paper
                                    key={idx}
                                    elevation={1}
                                    sx={{
                                        p: 1,
                                        mb: 0.5,
                                        bgcolor: 'primary.light',
                                        color: 'primary.contrastText',
                                        borderRadius: 1,
                                        cursor: 'default',
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    <Typography variant="caption" color="rgba(255,255,255,0.8)" display="block">
                                        {session.startTime} - {session.endTime}
                                    </Typography>
                                    <Typography variant="caption" fontWeight="bold" display="block" noWrap>
                                        {session.title}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
